<script setup lang="ts">
import type { PremiumPlan, PremiumPlanId } from '~/types/premium'
import { normalizeWelcomeCode, type JobPackLead } from '~/constants/pricing'

const props = defineProps<{
  modelValue: boolean
  initialPlan?: PremiumPlanId
  source?: 'cv-complete' | 'ai-limit' | 'default'
  discountCode?: string
  /**
   * What the visitor just ran into (a watermark, the CV limit, the AI limit…).
   * The heading speaks to it and the pack's stack opens with it.
   */
  lead?: JobPackLead
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
  /** The purchase has landed on the account; the page can act on it now. */
  (e: 'purchased', planId: PremiumPlanId): void
}>()

const { t } = useI18n()
const user = useSupabaseUser()
/** Only the welcome e-mail's code (HELLO-…) is still sold; legacy codes were tied to the retired monthly plan. */
const welcomeCode = computed(() => normalizeWelcomeCode(props.discountCode))
const { createCheckoutSession, isScriptLoading } = useLemonSqueezy()
const { redirectToSignup } = useRequireAccount()
const { plans: planList, price, defaultPlanId, yearlyPerMonth, getPlan, jobPackPrice } = usePremiumPlans()
const { formatCount, stats } = usePublicStats()
const { isPremium, exportCredits, continuationUntil, checkPremiumStatus } = usePremiumStatus()

/**
 * Premium month by month leads the list while this account's pack is ending
 * or just ended (PACK_CONTINUATION); nobody else is offered it.
 */
const continuation = computed(() => continuationUntil.value ? getPlan('pack-monthly') : undefined)
const plans = computed<PremiumPlan[]>(() => continuation.value ? [continuation.value, ...planList.value] : planList.value)
const continuationEnds = computed(() => continuationUntil.value ? new Date(continuationUntil.value).toLocaleDateString() : '')
const { trackPurchase } = useAdsConversion()

// Track premium checkout modal display
const trackPremiumCheckoutShown = async () => {
  try {
    // Check if already tracked
    const response = await $fetch('/api/user/premium-checkout', {
      method: 'GET'
    })

    // If not tracked yet, track it now
    if (!response.wasShown) {
      await $fetch('/api/user/premium-checkout', {
        method: 'POST'
      })
    }
  } catch (error) {
    console.error('Error tracking premium checkout:', error)
  }
}

const heading = computed(() => props.lead
  ? t(`premium.jobPack.lead.${props.lead}.title`)
  : t('premium.description'))
const subheading = computed(() => props.lead
  ? t(`premium.jobPack.lead.${props.lead}.text`)
  : t('premium.upsell.subheading.default'))

const hasPack = computed(() => !!getPlan('job-pack'))

const selectedPlanId = ref<PremiumPlanId>(props.initialPlan ?? defaultPlanId.value)
// The offer is known only once the account's plan is: then it is preselected,
// unless the page asked for a plan of its own.
watch(continuation, (plan) => {
  if (plan && !props.initialPlan) selectedPlanId.value = plan.id
})
const loading = ref(false)
const errorMessage = ref('')
const checkoutUrl = ref<string | null>(null)

const selectedPlan = computed<PremiumPlan | undefined>(() =>
  plans.value.find(plan => plan.id === selectedPlanId.value)
  ?? plans.value.find(plan => plan.id === defaultPlanId.value)
  ?? plans.value[0]
)

const isExportCreditSelected = computed(() => selectedPlan.value?.grantsExportCredit === true)

const gridClass = computed(() => {
  const n = plans.value.length
  if (n >= 4) return 'sm:grid-cols-2 lg:grid-cols-4'
  if (n === 3) return 'sm:grid-cols-3'
  if (n === 2) return 'sm:grid-cols-2'
  return ''
})

function intervalLabel(plan: PremiumPlan): string {
  if (!plan.interval) return ''
  return t(plan.interval === 'month' ? 'premium.month' : 'premium.year')
}

function ctaLabel(plan: PremiumPlan): string {
  if (plan.grantsExportCredit) return t('premium.upsell.oneTimeExport.cta', { price: price(plan) })
  if (plan.id === 'job-pack') return t('premium.jobPack.cta', { price: price(plan) })
  if (plan.id === 'pack-monthly') return t('premium.packMonthly.cta', { price: price(plan) })
  return `${t('premium.upgrade')} - ${price(plan)} / ${intervalLabel(plan)}`
}

if (import.meta.client) {
  watch(() => props.initialPlan, (value) => {
    if (value) {
      selectedPlanId.value = value
    }
  }, { immediate: true })

  watch(() => props.modelValue, async (value) => {
    if (value) {
      if (props.initialPlan) {
        selectedPlanId.value = props.initialPlan
      }
      document.body.style.overflow = 'hidden'

      // Track that premium checkout modal was shown
      await trackPremiumCheckoutShown()
    } else {
      document.body.style.overflow = ''
      errorMessage.value = ''
      loading.value = false
    }
  }, { immediate: true })

  onUnmounted(() => {
    document.body.style.overflow = ''
    stopPoll()
  })
}

/**
 * The checkout runs in an iframe, so LemonSqueezy's own redirect lands inside
 * that frame: the page around it would never learn that the payment went
 * through. The account is watched instead — the moment the webhook grants
 * Premium or an export credit, the modal closes itself and tells the page what
 * was bought, the same way the landing page waits for a guest purchase.
 */
const POLL_INTERVAL_MS = 3000
/** ~5 minutes; long enough for a slow card, short enough to stop by itself. */
const MAX_POLLS = 100

let poll: ReturnType<typeof setInterval> | null = null
let baseline = { isPremium: false, exportCredits: 0 }
let announced = false

function stopPoll() {
  if (poll) {
    clearInterval(poll)
    poll = null
  }
}

/** True once the account has gained something since this checkout opened. */
function hasNewPurchase() {
  return (isPremium.value && !baseline.isPremium) || exportCredits.value > baseline.exportCredits
}

function announce(planId: PremiumPlanId) {
  if (announced) return
  announced = true
  trackPurchase(planId)
  emit('purchased', planId)
}

async function watchPurchase(planId: PremiumPlanId) {
  stopPoll()
  announced = false
  await checkPremiumStatus()
  baseline = { isPremium: isPremium.value, exportCredits: exportCredits.value }

  let attempts = 0
  poll = setInterval(async () => {
    attempts++
    if (attempts > MAX_POLLS) {
      stopPoll()
      return
    }

    await checkPremiumStatus()
    if (!hasNewPurchase()) return

    stopPoll()
    announce(planId)
    close()
  }, POLL_INTERVAL_MS)
}

const close = () => {
  // Closing by hand can still mean "paid on the last screen", so the purchase
  // is checked once more before the watching stops.
  const wasCheckingOut = !!checkoutUrl.value && !announced
  const planId = selectedPlan.value?.id

  stopPoll()
  checkoutUrl.value = null
  emit('update:modelValue', false)
  emit('close')

  if (wasCheckingOut && planId) {
    checkPremiumStatus().then(() => {
      if (hasNewPurchase()) announce(planId)
    })
  }

  // Emit global event for app.vue to catch
  if (import.meta.client) {
    window.dispatchEvent(new CustomEvent('checkout-modal-closed', {
      detail: { source: props.source || 'default' }
    }))
  }
}

const selectPlan = (planId: PremiumPlanId) => {
  selectedPlanId.value = planId
}

const openCheckout = async () => {
  const plan = selectedPlan.value
  if (!plan) return
  // No session yet: sign-up explains why they are there and checkout resumes after.
  if (!user.value) {
    close()
    await redirectToSignup(plan.id)
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    // Fallback destination if the visitor ends up following LemonSqueezy's own
    // redirect: always this page, never the plan list they just bought from.
    // `export_paid` offers the download, `pack_paid` waits for Premium.
    const currentUrl = typeof window !== 'undefined' ? window.location.href : ''
    const sep = currentUrl.includes('?') ? '&' : '?'
    const redirectUrl = currentUrl
      ? `${currentUrl}${sep}${plan.grantsExportCredit ? 'export_paid=true' : 'pack_paid=true'}`
      : undefined

    const url = await createCheckoutSession({
      variantId: plan.variantId,
      userId: user.value.id,
      email: user.value.email || '',
      // The welcome code is for the pack alone (LemonSqueezy refuses it elsewhere).
      discountCode: plan.id === 'job-pack' ? welcomeCode.value ?? undefined : undefined,
      redirectUrl,
      planId: plan.id,
    })

    if (!url) {
      errorMessage.value = t('premium.checkoutError')
      loading.value = false
      return
    }

    checkoutUrl.value = url
    watchPurchase(plan.id)
  } catch (error) {
    console.error('Failed to open checkout', error)
    errorMessage.value = t('premium.checkoutError')
    loading.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <!--
      `sofax` on the overlay: the modal is teleported to <body>, outside every
      layout's wrapper, so it has to opt into the marketing styles itself.
    -->
    <div
      v-if="modelValue"
      class="sofax fixed inset-0 z-[1200] flex items-start justify-center bg-black/60 p-4 pt-1 backdrop-blur-sm sm:items-center sm:pt-0"
      @click="close"
    >
      <div
        class="relative max-h-[90vh] w-full overflow-hidden rounded-[0.625rem] bg-white shadow-lift dark:bg-gray-800"
        :class="checkoutUrl ? 'max-w-5xl' : 'max-w-3xl'"
        @click.stop
      >
        <button
          type="button"
          class="social-link absolute right-4 top-4 z-10 shadow-lift"
          :aria-label="t('common.close')"
          @click="close"
        >
          <i class="bi bi-x-lg" />
        </button>

        <div v-if="!checkoutUrl" class="max-h-[90vh] overflow-y-auto p-6 md:p-10">
          <div class="mx-auto max-w-2xl">
            <div class="mb-6 text-center">
              <span class="eyebrow mb-4">
                <i class="bi bi-stars text-accent" />
                {{ t('premium.title') }}
              </span>
              <h2 class="mb-3 !text-2xl md:!text-3xl">{{ heading }}</h2>
              <p class="text-base text-muted dark:text-gray-400" :class="{ 'hidden sm:block': !lead }">
                {{ subheading }}
              </p>
            </div>

            <!-- Social proof: real user count from the database, nothing invented -->
            <div v-if="stats?.users" class="mb-8 flex items-center justify-center gap-3">
              <SofaxIconTile icon="bi-people-fill" round size="sm" />
              <span class="!text-sm font-semibold text-ink dark:text-white">
                {{ t('premium.socialProof.users', { count: formatCount(stats.users) }) }}
              </span>
            </div>

            <!-- Plan cards, small version of the template's pricing card -->
            <div class="grid grid-cols-1 gap-4" :class="gridClass">
              <button
                v-for="plan in plans"
                :key="plan.id"
                type="button"
                :aria-pressed="selectedPlanId === plan.id"
                class="relative flex flex-col rounded-[0.625rem] border bg-white p-5 text-left transition-all duration-300 hover:shadow-lift focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:bg-gray-800"
                :class="selectedPlanId === plan.id ? 'border-ink dark:border-white' : 'border-line dark:border-gray-700'"
                @click="selectPlan(plan.id)"
              >
                <span v-if="plan.recommended" class="tag mb-3">{{ t('premium.recommended') }}</span>

                <span class="mb-2 block font-title text-base font-semibold text-ink dark:text-white">
                  {{ t(plan.nameKey) }}
                </span>

                <span class="display-heading display-heading-3 block">{{ price(plan) }}</span>
                <span class="mt-1 block !text-sm text-muted dark:text-gray-400">
                  {{ plan.interval ? `/ ${intervalLabel(plan)}` : t('premium.oneTimePayment') }}
                </span>

                <span v-if="plan.id === 'yearly'" class="mt-2 block !text-xs font-semibold text-accent">
                  {{ t('premium.perMonth', { price: yearlyPerMonth }) }}
                </span>

                <span v-if="plan.badgeKey" class="eyebrow mt-3 !px-3 !py-1 !text-xs">
                  {{ t(plan.badgeKey) }}
                </span>

                <span
                  v-if="selectedPlanId === plan.id"
                  class="absolute right-4 top-4 inline-flex size-6 items-center justify-center rounded-full bg-accent text-white"
                >
                  <i class="bi bi-check2 text-sm text-white" />
                </span>
              </button>
            </div>

            <SofaxBtn
              v-if="selectedPlan"
              variant="accent"
              block
              class="mt-6"
              :disabled="loading || isScriptLoading"
              :label="loading || isScriptLoading ? t('common.loading') : ctaLabel(selectedPlan)"
              @click="openCheckout"
            />

            <!-- Explanation panels for the one-time products -->
            <Transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="-translate-y-1 opacity-0"
              enter-to-class="translate-y-0 opacity-100"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="translate-y-0 opacity-100"
              leave-to-class="-translate-y-1 opacity-0"
            >
              <SofaxNotice v-if="isExportCreditSelected" type="info" plain class="mt-4">
                <span class="mb-2 block !text-xs font-semibold uppercase tracking-wide text-ink dark:text-white">
                  {{ t('premium.upsell.oneTimeExport.howItWorksTitle') }}
                </span>
                <ul class="space-y-1.5">
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.upsell.oneTimeExport.howItWorks1') }}
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.upsell.oneTimeExport.howItWorks2') }}
                  </li>
                </ul>
              </SofaxNotice>
              <SofaxNotice v-else-if="selectedPlan?.id === 'job-pack'" type="info" plain class="mt-4">
                <span class="mb-2 block !text-xs font-semibold uppercase tracking-wide text-ink dark:text-white">
                  {{ t('premium.jobPack.howItWorksTitle') }}
                </span>
                <ul class="space-y-1.5">
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.jobPack.howItWorks1', { days: selectedPlan.grantsPremiumDays }) }}
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.jobPack.howItWorks2') }}
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.jobPack.howItWorks3') }}
                  </li>
                </ul>
              </SofaxNotice>
              <SofaxNotice v-else-if="selectedPlan?.id === 'pack-monthly'" type="info" plain class="mt-4">
                <span class="mb-2 block !text-xs font-semibold uppercase tracking-wide text-ink dark:text-white">
                  {{ t('premium.jobPack.howItWorksTitle') }}
                </span>
                <ul class="space-y-1.5">
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.packMonthly.howItWorks1', { price: price(selectedPlan), packPrice: jobPackPrice }) }}
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.packMonthly.howItWorks2') }}
                  </li>
                  <li class="flex items-start gap-2">
                    <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-accent" />
                    {{ t('premium.packMonthly.howItWorks3', { date: continuationEnds }) }}
                  </li>
                </ul>
              </SofaxNotice>
            </Transition>

            <!-- Discount Code Badge (if provided) -->
            <SofaxNotice v-if="welcomeCode" type="success" plain class="mt-4">
              <div class="flex flex-wrap items-center justify-center gap-2">
                <i class="bi bi-tag-fill text-accent" />
                <span class="font-semibold">{{ t('premium.discountApplied') }}</span>
                <code class="rounded bg-white px-2 py-1 font-mono text-sm font-bold text-ink dark:bg-gray-700 dark:text-white">
                  {{ welcomeCode }}
                </code>
              </div>
            </SofaxNotice>

            <SofaxNotice v-if="errorMessage" type="error" class="mt-4">{{ errorMessage }}</SofaxNotice>

            <ul class="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted dark:text-gray-400">
              <li class="inline-flex items-center gap-2">
                <i class="bi bi-shield-fill-check text-accent" />
                {{ t('premium.securePayment') }}
              </li>
              <li v-if="selectedPlan && !selectedPlan.isOneTime" class="inline-flex items-center gap-2">
                <i class="bi bi-arrow-repeat text-accent" />
                {{ t('premium.upsell.badges.cancelAnytime') }}
              </li>
              <li v-else class="inline-flex items-center gap-2">
                <i class="bi bi-x-circle text-accent" />
                {{ t('premium.noAutoRenew') }}
              </li>
            </ul>

            <!-- What the pack unlocks, opening with whatever was just blocked -->
            <div v-if="hasPack" class="mt-8 border-t border-line pt-8 dark:border-gray-700">
              <div class="mb-5 text-center font-title text-lg font-semibold text-ink dark:text-white">
                {{ t('premium.jobPack.stack.title') }}
              </div>
              <PremiumJobPackStack :lead="lead" class="mx-auto max-w-lg" />
            </div>
          </div>
        </div>

        <!-- The checkout (LemonSqueezy, or Stripe in crowns) in the same frame -->
        <div v-else class="relative h-[90vh] w-full overflow-hidden rounded-[0.625rem]">
          <CommonCheckoutLoading />
          <CommonCheckoutEmbed :src="checkoutUrl" />
        </div>
      </div>
    </div>
  </Teleport>
</template>
